import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import QrScanner from "qr-scanner";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { scanQRCodePoint } from "../../services/authService";
import { fetchPointDataAction } from "../../store/app/pointActions";

/**
 * QRCodeModal
 *
 * Single unified modal:
 * - Attempts to start the camera via qr-scanner
 * - If camera is unavailable / permission denied → falls back to manual text input
 * - Renders via ReactDOM.createPortal so it always overlays the full screen
 *   regardless of the parent component's stacking context.
 */
function QRCodeModal({ onScan, onClose }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const dispatch = useDispatch();
  const [mode, setMode] = useState("loading"); // 'loading' | 'camera' | 'manual'
  const [manualValue, setManualValue] = useState("");
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraKey, setCameraKey] = useState(0); // Kamerayı yeniden başlatmak için

  useEffect(() => {
    let scanner;

    const tryCamera = async () => {
      const hasCamera = await QrScanner.hasCamera().catch(() => false);

      if (!hasCamera) {
        setMode("manual");
        return;
      }

      if (!videoRef.current) {
        setMode("manual");
        return;
      }

      try {
        scanner = new QrScanner(
          videoRef.current,
          async (result) => {
            // Çoklu taramayı engellemek için katmanlı kontrol
            if (!isScanning || isProcessing) return;

            const value = result?.data ?? result;
            if (!value || typeof value !== "string" || value.trim() === "") {
              return;
            }

            // Hemen taramayı durdur ve işlem başlat
            setIsScanning(false);
            setIsProcessing(true);

            // Tarayıcıyı hemen durdur
            if (scannerRef.current) {
              scannerRef.current.stop();
            }

            try {
              const res = await scanQRCodePoint(value.trim());
              const raw = res?.toJSON?.() ?? res;
              const result = raw?.result ?? raw;
              const successMessage = result?.message || raw?.message;
              const pointsEarned = result?.pointsEarned ?? result?.pointValue;
              const displayText = pointsEarned != null ? `${successMessage}\nKazandığınız puan: ${pointsEarned}` : successMessage;

              onScan(value.trim());
              onClose();

              if (displayText) {
                setTimeout(() => {
                  Swal.fire({
                    title: "Başarılı!",
                    text: displayText,
                    icon: "success",
                    confirmButtonText: "Tamam",
                    allowOutsideClick: false,
                  });
                }, 150);
              }
              await dispatch(fetchPointDataAction());
            } catch (err) {
              console.error("QR kodu işlenirken hata:", err);
              setError(err?.message || "QR kodu işlenemedi. Lütfen tekrar deneyin.");
            } finally {
              setIsProcessing(false);
            }
          },
          {
            preferredCamera: "environment",
            highlightScanRegion: true,
            highlightCodeOutline: true,
          },
        );
        scannerRef.current = scanner;
        await scanner.start();
        setMode("camera");
      } catch (err) {
        console.error("Kamera başlatılamadı:", err);
        setMode("manual");
      }
    };

    tryCamera();

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, [cameraKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualValue.trim()) return;

    try {
      const res = await scanQRCodePoint(manualValue.trim());
      const raw = res?.toJSON?.() ?? res;
      const result = raw?.result ?? raw;
      const successMessage = result?.message || raw?.message;
      const pointsEarned = result?.pointsEarned ?? result?.pointValue;
      const displayText = pointsEarned != null ? `${successMessage}\nKazandığınız puan: ${pointsEarned}` : successMessage;

      onScan(manualValue.trim());
      onClose();

      if (displayText) {
        setTimeout(() => {
          Swal.fire({
            title: "Başarılı!",
            text: displayText,
            icon: "success",
            confirmButtonText: "Tamam",
            allowOutsideClick: false,
          });
        }, 150);
      }
      await dispatch(fetchPointDataAction());
    } catch (err) {
      console.error("QR kodu işlenirken hata:", err);
      setError(err?.message || "QR kodu işlenemedi. Lütfen tekrar deneyin.");
    }
  };

  const handleRetry = () => {
    setError(null);
    setIsScanning(true);
    setIsProcessing(false);
    setCameraKey(prev => prev + 1); // Kamerayı yeniden başlat
  };

  const content = (
    <div className="fixed inset-0 flex items-center justify-center bg-primary-black bg-opacity-90" style={{ zIndex: 9999 }}>
      <div className="bg-white w-full max-w-sm mx-4 rounded-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-background-teal">
          <h3 className="text-base font-semibold uppercase text-primary-black">{mode === "camera" ? "QR Kod Tara" : mode === "manual" ? "QR Kod Gir" : "Hazırlanıyor..."}</h3>
          <button type="button" onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-full bg-white bg-opacity-60 hover:bg-opacity-100 transition">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.7504 4.75042L13.9445 11.5563L20.7504 18.3623L18.8059 20.3068L12 13.5008L5.19407 20.3068L3.24958 18.3623L10.0555 11.5563L3.24958 4.75042L5.19407 2.80593L12 9.61186L18.8059 2.80593L20.7504 4.75042Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        {/* Error Modal */}
        {error && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-red-600">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 15h2v2h-2v-2zm0-8h2v6h-2V9z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-primary-black mb-2">Hata Oluştu</h4>
            <p className="text-sm text-secondary-titles mb-6">{error}</p>
            <div className="flex gap-3">
              <button type="button" onClick={handleRetry} className="flex-1 bg-background-teal text-black py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors">
                Tekrar Dene
              </button>
              <button type="button" onClick={onClose} className="flex-1 bg-gray-200 text-primary-black py-2 px-4 rounded-md hover:bg-gray-300 transition-colors">
                Kapat
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {!error && mode === "loading" && (
          <div className="flex items-center justify-center p-8">
            <div className="w-8 h-8 border-4 border-background-teal border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Camera mode */}
        {/* video is always in DOM so useEffect ref resolves correctly */}
        {!error && (
          <div style={{ display: mode === "camera" ? "block" : "none" }} className="relative bg-black">
            <video ref={videoRef} className="w-full" style={{ maxHeight: "60vw", objectFit: "cover" }} playsInline muted />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="border-2 border-white border-opacity-70 rounded" style={{ width: "60%", aspectRatio: "1/1" }} />
            </div>
          </div>
        )}
        {!error && mode === "camera" && (
          <div className="px-6 py-4 text-center">
            {error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : (
              <>
                <p className="text-sm text-secondary-titles mb-3">QR kodu çerçeveye getirin</p>
                <button type="button" className="text-base text-primary-blue hover:text-primary-blue/80 transition-colors" onClick={() => setMode("manual")}>
                  Manuel Kod Girişi
                </button>
              </>
            )}
          </div>
        )}

        {/* Manual mode */}
        {!error && mode === "manual" && (
          <form onSubmit={handleManualSubmit} className="p-6 flex flex-col gap-4">
            <p className="text-xs text-secondary-titles">QR kod içeriğini aşağıya yazın veya yapıştırın.</p>
            <input
              type="text"
              className="w-full border border-secondary-lines px-3 py-2 text-xs focus:outline-none focus:border-primary-green"
              placeholder="QR kod değeri..."
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              autoFocus
            />
            <button type="submit" className="form-submit-btn uppercase w-full" style={{ maxWidth: "100%" }} disabled={!manualValue.trim()}>
              Gönder
            </button>
          </form>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default QRCodeModal;
